import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handlePaginatedRequest, handleRequest } from '../quipli-service-client';
import { Duration } from 'luxon';
import { AxiosRequestConfig } from 'axios';
import { Pagination } from '../pagination/pagination';
import {
  AccountingBasis,
  AccrualBasisTaxReportRow,
  AdminCommission,
  CashBasisTaxReportRow,
  CustomerSearchesReportRequest,
  CustomerSearchesReportRow,
  DailyPaymentReportRequest,
  DailyPaymentReportRow,
  DateTimeRange,
  DeliveryRevenueReportRequest,
  DeliveryRevenueReportRow,
  PaymentReportRequest,
  PaymentReportRow,
  PaymentReportSummary,
  ProductReportRequest,
  ProductReportRow,
  ProductReportSummary,
  ProductUnitsReportRow,
  ProductUnitsReportSummary,
  RenterReportRequest,
  RenterReportRow,
  ScheduledReport,
  ScheduledReportJson,
  SearchCommissionRequest,
  StripePayoutReconciliationReportRequest,
  StripePayoutReconciliationReportRow,
  StripePayoutReconciliationReportSummary,
  StripePayoutReconciliationReportTransaction,
  TaxExemptReportRow,
  TaxReportRequest,
} from './report';
import { DateTime } from 'luxon';
import { SearchProductInventoryUnitsRequest } from '../product/product';
import { ReportParsers } from './report-parsers';

export const useReportApiV2 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  function paymentReportHelper(req: PaymentReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/payments`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function paymentsReport(req: PaymentReportRequest): Promise<Pagination<PaymentReportRow>> {
    const config = paymentReportHelper(req);

    return await handlePaginatedRequest<PaymentReportRow>(config);
  }

  async function exportPaymentsReport(req: PaymentReportRequest): Promise<BlobPart> {
    const config = paymentReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function paymentsSummary(req: PaymentReportRequest): Promise<PaymentReportSummary> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/paymentsSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return handleRequest<PaymentReportSummary>(config);
  }

  const customerSearchesReportHelper = (req: CustomerSearchesReportRequest): AxiosRequestConfig => {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/customerSearches`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  };

  async function customerSearchesReport(
    req: CustomerSearchesReportRequest,
  ): Promise<Pagination<CustomerSearchesReportRow>> {
    const config = customerSearchesReportHelper(req);

    return await handlePaginatedRequest<CustomerSearchesReportRow>(config);
  }

  async function exportCustomerSearchesReport(req: RenterReportRequest): Promise<BlobPart> {
    const config = customerSearchesReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function rentersReportHelper(req: RenterReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/renters`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function rentersReport(req: RenterReportRequest): Promise<Pagination<RenterReportRow>> {
    const config = rentersReportHelper(req);

    return await handlePaginatedRequest<RenterReportRow>(config);
  }

  async function exportRentersReport(req: RenterReportRequest): Promise<BlobPart> {
    const config = rentersReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function cashBasisTaxReportHelper(req: TaxReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/cashBasisTax`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function cashBasisTaxReport(req: TaxReportRequest): Promise<CashBasisTaxReportRow[]> {
    const config = cashBasisTaxReportHelper(req);

    return await handleRequest<CashBasisTaxReportRow[]>(config);
  }

  async function exportCashBasisTaxReport(req: TaxReportRequest): Promise<BlobPart> {
    const config = cashBasisTaxReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function accrualBasisTaxReportHelper(req: TaxReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/accrualBasisTax`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function accrualBasisTaxReport(req: TaxReportRequest): Promise<AccrualBasisTaxReportRow[]> {
    const config = accrualBasisTaxReportHelper(req);

    return await handleRequest<AccrualBasisTaxReportRow[]>(config);
  }

  async function exportAccrualBasisTaxReport(req: TaxReportRequest): Promise<BlobPart> {
    const config = accrualBasisTaxReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function getAvailableTaxReportRange(basis: AccountingBasis): Promise<DateTimeRange> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/reports/availableTaxReportRange`,
      headers: {
        'X-Tenant': tenant,
      },
      params: { basis },
    };

    const raw = await handleRequest<{ from: string; to: string }>(config);

    return {
      from: DateTime.fromISO(raw.from, { setZone: true }),
      to: DateTime.fromISO(raw.to, { setZone: true }),
    };
  }

  function productReportHelper(req: ProductReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/products`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function productReport(req: ProductReportRequest): Promise<Pagination<ProductReportRow>> {
    const config = productReportHelper(req);

    return await handlePaginatedRequest<ProductReportRow>(config);
  }

  async function exportProductReport(req: ProductReportRequest): Promise<BlobPart> {
    const config = productReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function productSummary(req: ProductReportRequest): Promise<ProductReportSummary> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/productsSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return handleRequest<ProductReportSummary>(config);
  }

  function dailyPaymentReportHelper(req: DailyPaymentReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/dailyPayments`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function dailyPaymentsReport(req: DailyPaymentReportRequest): Promise<Pagination<DailyPaymentReportRow>> {
    const config = dailyPaymentReportHelper(req);

    return await handlePaginatedRequest<DailyPaymentReportRow>(config);
  }

  async function exportDailyPaymentsReport(req: DailyPaymentReportRequest): Promise<BlobPart> {
    const config = dailyPaymentReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function productUnitsSummaryHelper(req: SearchProductInventoryUnitsRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/productUnitsSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  function productUnitsReportHelper(req: SearchProductInventoryUnitsRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/productUnits`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function productUnitsReport(
    req: SearchProductInventoryUnitsRequest,
  ): Promise<Pagination<ProductUnitsReportRow>> {
    const config = productUnitsReportHelper(req);
    const res = await handlePaginatedRequest<ReportParsers.ProductUnitsReportRowJson>(config);
    return { items: res.items.map(ReportParsers.toProductUnitsReportRow), pagination: res.pagination };
  }

  async function productUnitsSummary(req: SearchProductInventoryUnitsRequest): Promise<ProductUnitsReportSummary> {
    const config = productUnitsSummaryHelper(req);

    return await handleRequest({ ...config });
  }

  async function exportProductUnitsReport(req: SearchProductInventoryUnitsRequest): Promise<BlobPart> {
    const config = productUnitsReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function stripePayoutReconciliationReportHelper(req: StripePayoutReconciliationReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/stripePayoutReconciliationReport`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function stripePayoutReconciliationSummary(
    req: StripePayoutReconciliationReportRequest,
  ): Promise<StripePayoutReconciliationReportSummary[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/stripePayoutReconciliationSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return await handleRequest<StripePayoutReconciliationReportSummary[]>(config);
  }

  async function stripePayoutReconciliationReport(
    req: StripePayoutReconciliationReportRequest,
  ): Promise<Pagination<StripePayoutReconciliationReportRow>> {
    const config = stripePayoutReconciliationReportHelper(req);

    return await handlePaginatedRequest<StripePayoutReconciliationReportRow>(config);
  }

  async function exportStripePayoutReconciliationReport(
    req: StripePayoutReconciliationReportRequest,
  ): Promise<BlobPart> {
    const config = stripePayoutReconciliationReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  function stripePayoutTransactionReportHelper(payoutId: string): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/stripePayoutTransactionReport/${payoutId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
  }

  async function stripePayoutTransactionReport(
    payoutId: string,
  ): Promise<StripePayoutReconciliationReportTransaction[]> {
    const config = stripePayoutTransactionReportHelper(payoutId);

    return await handleRequest<StripePayoutReconciliationReportTransaction[]>(config);
  }

  async function exportStripePayoutTransactionReport(payoutId: string): Promise<BlobPart> {
    const config = stripePayoutTransactionReportHelper(payoutId);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function getAvailableOrdersReportRange(): Promise<DateTimeRange> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/reports/availableOrdersReportRange`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const raw = await handleRequest<{ from: string; to: string }>(config);

    return {
      from: DateTime.fromISO(raw.from, { setZone: true }),
      to: DateTime.fromISO(raw.to, { setZone: true }),
    };
  }

  function taxExemptReportHelper(req: TaxReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/taxExempt`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function taxExemptReport(req: TaxReportRequest): Promise<Pagination<TaxExemptReportRow>> {
    const config = taxExemptReportHelper(req);

    return await handlePaginatedRequest<TaxExemptReportRow>(config);
  }

  async function exportTaxExemptReport(req: TaxReportRequest): Promise<BlobPart> {
    const config = taxExemptReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function deliveryRevenueSummary(
    req: DeliveryRevenueReportRequest,
  ): Promise<{ location: string; revenue: string }[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/deliveryRevenueSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return handleRequest<{ location: string; revenue: string }[]>(config);
  }

  function deliveryRevenueReportHelper(req: DeliveryRevenueReportRequest): AxiosRequestConfig {
    return {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/deliveryRevenue`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
  }

  async function deliveryRevenueReport(
    req: DeliveryRevenueReportRequest,
  ): Promise<Pagination<DeliveryRevenueReportRow>> {
    const config = deliveryRevenueReportHelper(req);

    return await handlePaginatedRequest<DeliveryRevenueReportRow>(config);
  }

  async function exportDeliveryRevenueReport(req: DeliveryRevenueReportRequest): Promise<BlobPart> {
    const config = deliveryRevenueReportHelper(req);

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function getSalesCommissionReport(data: SearchCommissionRequest): Promise<AdminCommission[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/salesCommission`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...data, startDate: data.startDate.toISODate(), endDate: data.endDate.toISODate() },
    };

    return (await handleRequest<ReportParsers.AdminCommissionJson[]>(config)).map(ReportParsers.toAdminCommission);
  }

  async function exportSalesCommissionReport(data: SearchCommissionRequest): Promise<BlobPart> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/salesCommission`,
      headers: {
        'X-Tenant': tenant,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      },
      responseType: 'blob',
      data: { ...data, startDate: data.startDate.toISODate(), endDate: data.endDate.toISODate() },
    };
    return await handleRequest(config, true);
  }

  async function scheduleDailyReport(data: {
    reportId: string;
    deliveryInterval: Duration;
    parameters?: Record<string | number, any>;
  }): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/reports/schedule/scheduledReports`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };

    return handleRequest(config);
  }

  async function listScheduledReports(): Promise<ScheduledReport[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/reports/schedule/scheduledReports`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const resp = await handleRequest<ScheduledReportJson[]>(config);
    return resp.map(ReportParsers.toScheduledReport);
  }

  async function getScheduledReport(id: string): Promise<ScheduledReport> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/reports/schedule/scheduledReports/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const resp = await handleRequest<ScheduledReportJson>(config);
    return ReportParsers.toScheduledReport(resp);
  }

  async function deleteScheduledReport(id: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v2/reports/schedule/scheduledReports/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest(config);
  }

  async function updateScheduledReport(
    id: string,
    data: { deliveryInterval: Duration; parameters?: Record<string | number, any> },
  ): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v2/reports/schedule/scheduledReports/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };

    return handleRequest(config);
  }

  async function listReports(): Promise<{ id: string; name: string }[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/reports/schedule/reports`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest(config);
  }

  return {
    paymentsReport,
    exportPaymentsReport,
    paymentsSummary,
    dailyPaymentsReport,
    exportDailyPaymentsReport,
    rentersReport,
    customerSearchesReport,
    exportCustomerSearchesReport,
    exportRentersReport,
    cashBasisTaxReport,
    exportCashBasisTaxReport,
    accrualBasisTaxReport,
    exportAccrualBasisTaxReport,
    getAvailableTaxReportRange,
    productReport,
    exportProductReport,
    productSummary,
    productUnitsReport,
    productUnitsSummary,
    exportProductUnitsReport,
    stripePayoutReconciliationReport,
    stripePayoutReconciliationSummary,
    exportStripePayoutReconciliationReport,
    stripePayoutTransactionReport,
    exportStripePayoutTransactionReport,
    getAvailableOrdersReportRange,
    taxExemptReport,
    exportTaxExemptReport,
    deliveryRevenueSummary,
    deliveryRevenueReport,
    exportDeliveryRevenueReport,
    getSalesCommissionReport,
    exportSalesCommissionReport,
    scheduleDailyReport,
    listScheduledReports,
    getScheduledReport,
    deleteScheduledReport,
    updateScheduledReport,
    listReports,
  };
};
